import React, { lazy, useEffect, useRef, useState, useCallback, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { ApolloProvider } from '@apollo/client';
import apolloGraphQlClient from './GraphQL/DataAccessGraphQLClient';
import InactivityAlert from './CommonComponents/InactivityAlert/InactivityAlert';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { paramsDecoder } from './utils/urlHelpers';
import { getSecrets } from './utils/secrets';
import { useErrorBoundary } from "react-error-boundary"
import { useOnlineStatus } from './hooks/useOnlineStatus';
import ServiceHealthWatcher from './CommonComponents/ServiceHealthWatcher/ServiceHealthWatcher';
import OfflineOverlay from './containers/Overlays/OfflineOverLay';
import { setTriggerSave } from './redux/action/userActivityAction';
import { validateUser } from './redux/action/userAction';
import ProtectedRoute from './layout/ProtectedRoute';
import Loader from './CommonComponents/Loader/Loader';
import { openMoveCopySheetModalHelper } from './helpers/helper';

// Moved theme constant outside the component
const theme = createTheme({
  typography: {
    fontFamily: [
      "Arial",
      "Roboto",
      "sans-serif",
      "Helvetica",
    ].join(","),
  },

  components: {
    MuiMenu: {
      styleOverrides: {
        paper: {
          minWidth: 220,
          overflow: "visible",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '20px',
          maxHeight: '900px',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          overflow: "visible",
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          whiteSpace: "nowrap",
        },
      },
    },
  },
});

// Lazy loading of all the Page/Major Components
const Home = lazy(() => import('./pages/Home/Home'))
const Login = lazy(() => import('./pages/Login/Login'))
const NewSheet = lazy(() => import('./pages/Sheet/NewSheet'))
const Browse = lazy(() => import('./pages/BrowsePage/BrowsePage'))
const AccessHub = lazy(() => import('./pages/AccessHub/AccessHub'))
const TemplateLibrary = lazy(() => import('./pages/BrowsePage/Components/TemplateLibrary/TemplateLibrary'))


function App() {
  const sheetRef = useRef();
  const location = useLocation();
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const { currentSelectedWorkbook, isSharedWorkbook, workbookListData,sharedWithMeWorkBooks } = useSelector((state) => state.workbookReducer);
  const { currentSelectedSheet } = useSelector((state) => state.sheetReducer);
  const { currentSelectedWorkspace } = useSelector((state) => state.workspaceReducer);
  const { accessInfoById, fallbackerror } = useSelector((state) => state.userActivityReducer);


  const { userData, authLoading } = useSelector((state) => state.userReducer);
  const { isShared } = useSelector((state) => state.sheetReducer)
  const [openShareSheetPopup, setOpenShareSheetPopup] = useState(false)
  const [sharePopupType, setsharePopupType] = useState("sheet")
  const [shareTarget, setShareTarget] = useState(null);

  const [moveCopyModalOpen, setMoveCopyModalOpen] = useState(false);
  const [moveCopySheet, setMoveCopySheet] = useState(null);
  const [moveCopyWorkbook, setMoveCopyWorkbook] = useState(null);
  const [moveCopySheetList, setMoveCopySheetList] = useState([]);
  const [position, setPosition] = useState("at End");

  const [rowCount, setRowCount] = useState(0)
  const { showBoundary } = useErrorBoundary();

  const isOnline = useOnlineStatus();
  const prevIsOnline = useRef(isOnline);
  useEffect(() => {
    dispatch(validateUser());
  }, [dispatch]);



  useEffect(() => {
    if (prevIsOnline.current === false && isOnline === true) {

      dispatch(setTriggerSave(true));
    }
    prevIsOnline.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    if (fallbackerror) {
      showBoundary(fallbackerror);

    }
  }, [fallbackerror, showBoundary]);

  
  // Memoized callback for setting share popup state
  const handleSetOpenShareSheetPopup = useCallback((open, type) => {
    setOpenShareSheetPopup(open);
    setsharePopupType(type);
  }, []);

  useEffect(() => {
    dispatch(paramsDecoder())
  }, [dispatch])

  useEffect(() => {
    getSecrets()
  }, [])

  useEffect(() => {
    console.log("the location has changed", location)
  }, [location, navigate])

  useEffect(() => {
    let title = TitleText()
    document.title = `xSpaces ${title === undefined ? '' : TitleText()}`
    // Added all dependencies
  }, [currentSelectedSheet, location, isShared, accessInfoById, userData, currentSelectedWorkbook, currentSelectedWorkspace])

  const TitleText = () => {
    if (isShared) {
      let access = accessInfoById.find(item => item.user_id === userData?.email)
      let viewName = access?.view || ""
      const sheetName = (currentSelectedSheet?.user_id === userData?.email) ? (currentSelectedSheet?.name) : viewName
      return `- ${sheetName ?? ""}`
    }
    else if (location.pathname.includes("sheet")) {
      return `- ${currentSelectedWorkbook?.name === undefined ? "" : currentSelectedWorkbook.name}`
    }
    else if (location.pathname.includes("activity")) {
      return `- Notifications`
    }
    else if (location.pathname.includes("library")) {
      return `- Template Library`
    }
    else if (location.pathname.includes("browse")) {
      return `- ${currentSelectedWorkspace?.name ?? ""}`
    }
    else if (location.pathname.includes("login")) {
      return `- Login`
    }
  }

  if (authLoading) {
    return <Loader />;
  }

  const openMoveCopySheetModal = (sheet) => {
    openMoveCopySheetModalHelper(
      sheet,
      setMoveCopySheet,
      setMoveCopyWorkbook,
      setMoveCopySheetList,
      setMoveCopyModalOpen,
      workbookListData,
      sharedWithMeWorkBooks
      )
  };

  const closeMoveCopySheetModal = () => {
    setMoveCopySheet(null);
    setMoveCopyModalOpen(false);
    setMoveCopyWorkbook(null);
    setMoveCopySheetList([]);
    setPosition('at End')
  };

  return (
    <>
      <ApolloProvider client={apolloGraphQlClient}>
        <ThemeProvider theme={theme}>
          <OfflineOverlay open={!isOnline} />
          <ServiceHealthWatcher />
          {/* Overall app routes */}
          <Suspense fallback={<Loader />}>
            <Routes>
              <Route path="/" element={
                <ProtectedRoute>
                  <Home sheetRef={sheetRef}
                    openShareSheetPopup={openShareSheetPopup}
                    setOpenShareSheetPopup={(open, type) => {
                      handleSetOpenShareSheetPopup(open, type)
                    }}
                    setShareTarget={setShareTarget}
                    isShared={isShared}
                    isSharedWorkbook={isSharedWorkbook}
                    openMoveCopySheetModal = {(sheet) => {
                      openMoveCopySheetModal(sheet)
                    } }
                    closeMoveCopySheetModal={closeMoveCopySheetModal}
                    />
                </ProtectedRoute>
              } >
                <Route path="sheet/:id"
                  element={<NewSheet
                    ref={sheetRef}
                    setRowCount={setRowCount}
                    openShareSheetPopup={openShareSheetPopup}
                    setOpenShareSheetPopup={(open) => {
                      handleSetOpenShareSheetPopup(open, "workbook")
                    }}
                    sharePopupType={sharePopupType}
                    isShared={isShared}
                    setsharePopupType={setsharePopupType}
                    setShareTarget={setShareTarget}
                    shareTarget={shareTarget}
                    moveCopySheet={moveCopySheet}
                    moveCopyModalOpen={moveCopyModalOpen}
                    openMoveCopySheetModal = {(sheet) => {
                      openMoveCopySheetModal(sheet)
                    } }
                    closeMoveCopySheetModal={closeMoveCopySheetModal}
                    position={position}
                    moveCopySheetList={moveCopySheetList}
                    moveCopyWorkbook={moveCopyWorkbook}
                    setPosition={setPosition}

                  />} />
                <Route path="browse" element={<Browse />} />
                <Route path="activity" element={<AccessHub />} />
                <Route path="library" element={<TemplateLibrary />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Route>
              <Route path="login" element={<Login />} />
            </Routes>
          </Suspense>
        </ThemeProvider>
      </ApolloProvider>
      <InactivityAlert />
    </>
  );
}

export default App;